import React, { useEffect, useRef, useState, useMemo } from "react";
import { Box } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  addComment,
  fetchStructuredLogs,
  translateTexts,
} from "../../../redux/actions/conversationsActions";
import {
  toggleMessageTranslation,
  toggleMessageRedaction,
} from "../../../redux/reducers/conversationsReducer";
import {
  Message,
  Comment,
  LogSchema,
} from "../../../redux/reducers/conversationsReducer";
import { getTargetId } from "../../../utils/render";
import { format } from "date-fns";
import CryptoJS from "crypto-js";
import MessageContent from "./message-bubble/MessageContent";
import CommentsSection from "./message-bubble/CommentsSection";
import AddCommentModal from "./message-bubble/AddCommentModal";
import StructuredLogsModal from "./message-bubble/StructuredLogsModal";

interface MessageBubbleProps {
  message: Message;
  chatSessionId: string;
  speakerName?: string; // <-- New optional prop
  isRightAligned: boolean;
  conversationId?: string;
  canViewAllData?: boolean;
  canReadConversation?: boolean; // Whether user has admin-level read access
  comments?: Comment[];
  onHeightChange?: (height: number) => void;
  isHighlighted?: boolean;
  highlightedCommentId?: string | null;
  searchTerm?: string;
  onCommentAdded?: () => void;
  getContextMessages: (messageId: number, chatSessionId: string) => Message[];
  getSpeakerName: (msg: Message) => string;
  sessionLanguage?: string; // Primary language of the chat session
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  chatSessionId,
  speakerName,
  isRightAligned,
  conversationId,
  canViewAllData,
  canReadConversation,
  comments = [],
  onHeightChange,
  onCommentAdded,
  highlightedCommentId,
  searchTerm,
  getContextMessages,
  isHighlighted,
  getSpeakerName,
  sessionLanguage,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user: adminUser } = useSelector((state: RootState) => state.base);

  // Identify the message first (needed for other selectors)
  const messageId = message.id ?? message.chat_message_id;
  const chatSessionIdFromMessage = message?.chat_session_id ?? chatSessionId;
  const targetId = getTargetId(messageId, chatSessionIdFromMessage);

  // Permissions
  const canViewModelCalls = adminUser?.scopes?.includes("structuredlog:read");
  const canViewMessageAudio = adminUser?.scopes?.includes("storedaudio:read");

  // Translation state from Redux
  const translations = useSelector(
    (state: RootState) => state.conversations.translations
  );
  const translating = useSelector(
    (state: RootState) => state.conversations.translating
  );
  const showTranslationForMessage = useSelector(
    (state: RootState) => state.conversations.showTranslationForMessage
  );

  // Check if session needs translation (not English/default)
  const needsTranslation = Boolean(
    sessionLanguage && sessionLanguage !== "default" && sessionLanguage !== "english"
  );

  // Get show translation state for this specific message
  const showTranslation = showTranslationForMessage[targetId] || false;

  // Redaction state from Redux
  const showRedactedForMessage = useSelector(
    (state: RootState) => state.conversations?.showRedactedForMessage
  );

  // Check if message has redacted_content field (for toggle functionality in real data view)
  const hasRedactedContent = Boolean(message.redacted_content);

  // Check if we should show a redaction indicator (greyed out icon for transcript view)
  // This shows when in transcript view and content contains [REDACTED marker
  const showRedactionIndicator = !canViewAllData && Boolean(message.content?.includes("[REDACTED"));

  // Get show redacted state for this specific message (default to true = show redacted)
  const showRedacted = showRedactedForMessage?.[targetId] ?? true;

  // Image / logs state
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState(true);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);

  // Structured logs
  const structuredLogsProd = useSelector(
    (state: RootState) => state.conversations?.structuredLogs?.[targetId]
  );
  const loadingStructuredLogsProd = useSelector(
    (state: RootState) => state.conversations?.loadingStructuredLogs?.[targetId]
  );

  const loadingStructuredLogsDev = useSelector(
    (state: RootState) =>
      state.conversations?.loadingStructuredLogsDev?.[targetId]
  );

  const structuredLogsDev = useSelector(
    (state: RootState) => state.conversations?.structuredLogsDev?.[targetId]
  );

  const bubbleRef = useRef<HTMLDivElement>(null);

  // Recalculate height
  useEffect(() => {
    if (bubbleRef.current && onHeightChange) {
      const height = bubbleRef.current.getBoundingClientRect().height;
      onHeightChange(height);
    }
  }, [
    onHeightChange,
    comments.length,
    imageUrl,
    showComments,
    commentText,
    message.content,
    loadingImage,
  ]);

  // Expand comments if we highlight a specific one
  useEffect(() => {
    if (highlightedCommentId) {
      const hasHighlightedComment = comments.some(
        (comment) => comment.id === highlightedCommentId
      );
      if (hasHighlightedComment) {
        setShowComments(true);
      }
    }
  }, [highlightedCommentId, comments]);

  // Group comments by thread
  const threadsMap = comments.reduce((threads, comment) => {
    if (!threads[comment.thread_id]) {
      threads[comment.thread_id] = [];
    }
    threads[comment.thread_id].push(comment);
    return threads;
  }, {} as Record<string, Comment[]>);

  // Sort threads by latest updated
  const threads = Object.values(threadsMap).sort((a, b) => {
    const latestA = new Date(
      a.reduce(
        (max, comment) =>
          new Date(comment.created_at) > new Date(max)
            ? comment.created_at
            : max,
        a[0].created_at
      )
    );
    const latestB = new Date(
      b.reduce(
        (max, comment) =>
          new Date(comment.created_at) > new Date(max)
            ? comment.created_at
            : max,
        b[0].created_at
      )
    );
    return latestB.getTime() - latestA.getTime();
  });

  // Add a comment
  const handleAddComment = (tags: string[]) => {
    if (
      conversationId &&
      (message?.id != null || message.chat_message_id != null) &&
      commentText !== ""
    ) {
      dispatch(
        addComment({
          conversationId: conversationId,
          targetId,
          comment: commentText,
          tags: tags,
        })
      );
      setCommentText("");
      setShowCommentModal(false);
      setShowComments(true);
      onCommentAdded?.();
    }
  };

  // Possibly fetch an attached image from S3 or similar
  const fetchImage = async () => {
    setLoadingImage(true);
    try {
      const encryptedJwt = localStorage.getItem("jwt");
      let jwt = null;

      if (encryptedJwt) {
        const bytes = CryptoJS.AES.decrypt(
          encryptedJwt,
          process.env.REACT_APP_CRYPTO_SECRET_KEY ?? ""
        );
        jwt = bytes.toString(CryptoJS.enc.Utf8);
      }

      const headers: HeadersInit = { "Content-Type": "image/jpeg" };
      if (jwt) {
        headers["Authorization"] = `Bearer ${jwt}`;
      }

      const blobPath = message.media_ref?.blob_path;
      const response = await fetch(
        `${process.env.REACT_APP_MEDIA_SERVER_URL}v1/media/download_media?conversation_id=${conversationId}&blob_path=${blobPath}`,
        { method: "GET", headers: headers }
      );

      if (!response.ok) {
        throw new Error("Failed to download image");
      }
      const arrayBuffer = await response.arrayBuffer();
      const base64String = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ""
        )
      );
      setImageUrl(
        `data:${message.media_ref?.content_type};base64,${base64String}`
      );
    } catch (error) {
      console.error("Error fetching image:", error);
    } finally {
      setLoadingImage(false);
    }
  };

  // Copy link to this message
  const handleShareMessage = () => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_LOGIN_REDIRECT_URL}/conversations/${conversationId}?target_id=${targetId}`
    );
    setShowShareTooltip(true);
    setTimeout(() => setShowShareTooltip(false), 2000);
  };

  // Show logs
  const handleShowLogsModal = (schema?: LogSchema) => {
    setShowLogsModal(true);
    if (
      ((!structuredLogsProd && !loadingStructuredLogsProd) ||
        (!loadingStructuredLogsDev && !structuredLogsDev)) &&
      (message.client_msg_uid ||
        message.triggered_by_action_id ||
        message.responding_to)
    ) {
      dispatch(
        fetchStructuredLogs({
          conversationId: conversationId || "",
          chatSessionId: chatSessionIdFromMessage,
          startMsgId: messageId,
          requestedEvents: [
            { event: "llm_inference_event", orderings: ["CREATED_AT_DESC"] },
            { event: "model_inference_event", orderings: ["CREATED_AT_DESC"] },
            {
              event: "text_to_speech_saved_event",
              orderings: ["CREATED_AT_DESC"],
            },
            {
              event: "active_memory_search_event",
              orderings: ["CREATED_AT_DESC"],
            },
            {
              event: "audio_group_transcription_saved_event",
              orderings: ["CREATED_AT_DESC"],
            },
            {
              event: "templated_model_task_executed_event",
              orderings: ["CREATED_AT_DESC"],
            },
            {
              event: "ner_redaction_event",
              orderings: ["CREATED_AT_DESC"],
            },
          ],
          targetSchema: schema,
          actionId:
            message.type === "human"
              ? message.triggered_by_action_id ?? ""
              : message.responding_to
              ? message.responding_to
              : message.triggered_by_action_id ?? "",
          clientMsgUid: message.client_msg_uid ?? "",
        })
      );
    }
  };

  // Handle translation toggle
  const handleTranslateToggle = async () => {
    console.log("handleTranslateToggle called", {
      showTranslation,
      messageContent: message.content,
      translations,
      sessionLanguage,
      needsTranslation,
      targetId,
    });

    if (!showTranslation && message.content && translations && !translations[message.content]) {
      // Need to translate
      console.log("Calling translateTexts API...");
      try {
        const result = await dispatch(
          translateTexts({
            texts: [message.content],
            targetLanguage: "en",
            sourceLanguage: sessionLanguage,
          })
        ).unwrap();
        console.log("Translation result:", result);
      } catch (error) {
        console.error("Translation failed:", error);
        // Don't toggle if translation failed
        return;
      }
    }
    // Toggle the translation state in Redux
    dispatch(toggleMessageTranslation(targetId));
  };

  // Handle redaction toggle
  const handleRedactionToggle = () => {
    dispatch(toggleMessageRedaction(targetId));
  };

  // Determine content to display
  const contentToDisplay = useMemo(() => {
    // First, determine base content (redacted or original)
    let baseContent = message.content || "";
    if (hasRedactedContent && showRedacted) {
      baseContent = message.redacted_content || "";
    }

    // Then apply translation if needed
    if (showTranslation && baseContent && translations && translations[baseContent]) {
      return translations[baseContent];
    }
    return baseContent;
  }, [showTranslation, translations, message.content, message.redacted_content, hasRedactedContent, showRedacted]);

  // Highlighted content
  const highlightedContent = useMemo(() => {
    if (!searchTerm || !contentToDisplay) {
      return contentToDisplay;
    }
    const regex = new RegExp(`(${searchTerm})`, "gi");
    return contentToDisplay.split(regex).map((part, index) =>
      regex.test(part) ? (
        <span key={index} style={{ backgroundColor: "yellow" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  }, [searchTerm, contentToDisplay]);

  // Formatted date
  const formattedDate = format(
    new Date(message.created_at),
    "MMM d, yyyy HH:mm"
  );

  return (
    <Box
      ref={bubbleRef}
      sx={{ display: "flex", alignItems: "flex-start" }}
      id={targetId}
      className={isRightAligned ? "bot-bubble" : "human-bubble"}
    >
      <Box position="relative" flex={1}>
        <MessageContent
          message={message}
          isRightAligned={isRightAligned}
          canViewAllData={canViewAllData}
          canReadConversation={canReadConversation}
          imageUrl={imageUrl}
          loadingImage={loadingImage}
          fetchImage={fetchImage}
          highlightedContent={highlightedContent}
          formattedDate={formattedDate}
          canViewMessageAudio={canViewMessageAudio}
          showShareTooltip={showShareTooltip}
          handleShareMessage={handleShareMessage}
          comments={comments}
          showComments={showComments}
          setShowComments={setShowComments}
          canViewModelCalls={canViewModelCalls}
          handleShowLogsModal={handleShowLogsModal}
          setShowCommentModal={setShowCommentModal}
          isHighlighted={isHighlighted}
          speakerName={speakerName}
          needsTranslation={needsTranslation}
          showTranslation={showTranslation}
          handleTranslateToggle={handleTranslateToggle}
          translating={translating}
          hasRedactedContent={hasRedactedContent}
          showRedacted={showRedacted}
          handleRedactionToggle={handleRedactionToggle}
          showRedactionIndicator={showRedactionIndicator}
        />

        <CommentsSection
          threads={threads}
          showComments={showComments}
          setShowComments={setShowComments}
          conversationId={conversationId}
          searchTerm={searchTerm}
          onCommentChange={() => onCommentAdded?.()}
          onHeightChange={() => {
            if (bubbleRef.current && onHeightChange) {
              const height = bubbleRef.current.getBoundingClientRect().height;
              onHeightChange(height);
            }
          }}
        />

        {canViewModelCalls && (
          <StructuredLogsModal
            showLogsModal={showLogsModal}
            setShowLogsModal={setShowLogsModal}
            structuredLogs={structuredLogsProd}
            devStructuredLogs={structuredLogsDev}
            loadingStructuredLogs={loadingStructuredLogsProd}
            devLoadingStructuredLogs={loadingStructuredLogsDev}
            handleShowLogsModal={handleShowLogsModal}
            message={message}
            getContextMessages={getContextMessages}
            getSpeakerName={getSpeakerName}
          />
        )}

        <AddCommentModal
          showCommentModal={showCommentModal}
          setShowCommentModal={setShowCommentModal}
          commentText={commentText}
          setCommentText={setCommentText}
          handleAddComment={(tags) => {
            handleAddComment(tags);
          }}
        />
      </Box>
    </Box>
  );
};

export default MessageBubble;
